// install.js — amm-v3 (A1, ondata 28): motore del cantiere AMM-v3.
// WRAP ESTERNO flag-gated di AMM.renderPage — SEAM (b) della spike o27 §2.2,
// deciso con PROBE STRUMENTATO REALE (evidence a1-01-probe-seam.txt):
//
//  - renderPage (admin-module.js r.1084-1113) fa `this.currentPage = page` e
//    poi `el.innerHTML = 'Caricamento…'` PRIMA del dispatch: il container del
//    root React verrebbe svuotato dall'ESTERNO a ogni navigazione. MISURATO
//    (probe §4): root.render su container svuotato NON lancia ma NON
//    ripristina la vista (corruzione silenziosa) e il successivo unmount
//    lancia removeChild. Il seam (a) (wrap dei singoli render<Vista>)
//    richiederebbe un root NUOVO a ogni nav = doppio motore sullo stesso el.
//  - Col seam (b) il wrap decide PRIMA della wipe: pagine v3 ⇒ niente wipe,
//    root React persistente; pagine v1 ⇒ unmount del root PRIMA di delegare
//    all'orig (mai root ferito, mai vista bianca).
//  - I re-render interni del v1 (self.renderCruscotto dai chip, MISURATI nel
//    probe §3) esistono solo nel mondo v1: sotto flag v3 quegli handler non
//    esistono. Tutte le vie ESTERNE (sidebar/bridge, setWorkspace/restore,
//    openEditor post-save, esito bulk, ricercaGoto) passano da renderPage.
//  - Il wrap replica `this.currentPage = page` (stampa R5 beforeprint e
//    bridge-guard onActive lo leggono — spike R5).
//  - admin-module.js OFF-LIMITS (R4): ZERO byte cambiati, solo wrap esterno.
//
// UN SOLO WRAP, REGISTRY PER-VISTA: il primo install installa il wrapper
// (guardia __ammV3Wrapped) con una Map pagina→istanza v3; gli install delle
// viste successive (A2-A6) AGGIUNGONO al registry senza impilare wrapper.
//
// GUARDIE (in ordine, ognuna ⇒ bundle INERTE, return false — pattern proc-v3):
//  1. flag della vista !== '1' (DEFAULT OFF, flags.js);
//  2. shell v2 col bus assente (workspace-host legacy / bundle shell 404);
//  3. modulo 'amm' mai registrato (adapter amm-v0 assente/rotto);
//  4. React 18 UMD assente;
//  5. window.AMM assente o renderPage/render<Vista> non funzioni (rename nel
//     monolite: il contract test statico segnala il drift, qui si degrada a v1);
//  6. un metodo AMM delegato o un path api.amm manca (il v3 delega TUTTE le
//     azioni al v1: senza anche uno solo la vista sarebbe monca ⇒ inerte);
//  7. factory invalida (senza render/unmount/isMounted).
//
// CICLO DI VITA:
//  - renderPage(pagina v3) ⇒ currentPage aggiornato + v3.render() nel
//    container #amm-page (NIENTE wipe); errore ⇒ console.error + unmount +
//    fallback all'orig (mai vista bianca, mai silenzio);
//  - renderPage(pagina diversa) ⇒ unmount di ogni vista v3 montata PRIMA
//    dell'orig (il container torna libero per la wipe v1);
//  - cambio MODULO (amm→proc): NESSUN renderPage ⇒ non si smonta (il DOM è
//    solo nascosto da body.workspace-amm; al rientro setWorkspace richiama
//    renderPage ⇒ refresh v3) — coerente con proc-v3;
//  - catch-up: se la vista è GIÀ attiva all'install (v1 l'ha già dipinta),
//    un giro di renderPage riallinea il DOM (solo dentro il workspace amm).
//
// BADGE — MAI 'nav:badge' da qui o da client/src/amm-v3/** (emettitore unico:
// adapter amm-v0). Asserito dai contract test.

import { ammV3CruscottoEnabled, ammV3ScadenzarioEnabled, ammV3AnagraficheEnabled, ammV3CalendarioEnabled, ammV3RiepilogoEnabled, ammV3ResocontoEnabled, ammV3PersonaleEnabled, ammV3VociEnabled, ammV3EserciziEnabled, ammV3TributiEnabled, ammV3RateizzazioniEnabled, ammV3CantieriCostiEnabled, ammV3ProfiloEnabled, ammV3F24Enabled } from '../shell/flags.js';
import { AMM_DELEGATED_METHODS, AMM_API_PATHS, apiPathFn } from './api.js';
import { AS_DELEGATED_METHODS, AS_API_PATHS } from './scadenzario-api.js';
import { AN_DELEGATED_METHODS, AN_API_PATHS, AMM_ANAG_KEYS } from './anagrafiche-api.js';
import { AC_DELEGATED_METHODS, AC_API_PATHS } from './calendario-api.js';
import { AR_DELEGATED_METHODS, AR_API_PATHS } from './riepilogo-api.js';
import { ARS_DELEGATED_METHODS, ARS_API_PATHS } from './resoconto-api.js';
import { AP_DELEGATED_METHODS, AP_API_PATHS } from './personale-api.js';
import { AV_DELEGATED_METHODS, AV_API_PATHS } from './voci-api.js';
import { AE_DELEGATED_METHODS, AE_API_PATHS } from './esercizi-api.js';
import { TR_DELEGATED_METHODS, TR_API_PATHS } from './tributi-api.js';
import { RZ_DELEGATED_METHODS, RZ_API_PATHS } from './rateizzazioni-api.js';
import { CC_DELEGATED_METHODS, CC_API_PATHS } from './cantieri-costi-api.js';
import { PR_DELEGATED_METHODS, PR_API_PATHS } from './profilo-api.js';
import { F24_DELEGATED_METHODS, F24_API_PATHS } from './f24-api.js';

export function installAmmV3View(win, createV3, spec) {
  if (!win || !spec.enabled(win)) return false; // flag OFF (default): inerte
  const W = win.Workspaces;
  if (!W || W.version !== 2 || !W.bus || typeof W.bus.on !== 'function' || typeof W.has !== 'function') return false;
  if (!W.has('amm')) return false; // adapter amm-v0 mai registrato: mai wrap orfano
  if (!win.React || !win.ReactDOM || typeof win.ReactDOM.createRoot !== 'function') return false;
  const A = win.AMM;
  if (!A || typeof A.renderPage !== 'function') return false;
  if (typeof A[spec.renderName] !== 'function') return false;
  for (const m of spec.delegatedMethods) {
    if (typeof A[m] !== 'function') return false; // v1 monco: la vista delega tutto lì
  }
  for (const p of spec.delegatedApiPaths) {
    if (!apiPathFn(win, p)) return false;
  }
  if (spec.extraGuard && !spec.extraGuard(win)) return false; // guardia extra della vista (es. ARCHIVIABILI)
  const v3 = typeof createV3 === 'function' ? createV3(win) : null;
  if (!v3 || typeof v3.render !== 'function' || typeof v3.unmount !== 'function' || typeof v3.isMounted !== 'function') return false;

  // ---- wrap UNICO di renderPage (installato al primo install riuscito) ----
  let wrapped = A.renderPage;
  if (!wrapped.__ammV3Wrapped) {
    const orig = A.renderPage;
    const views = new Map(); // pagina → istanza v3
    // RAZZA MISURATA in collaudo (a1-03): a ogni nav dal sottomenù la shell
    // ri-attiva il modulo (proxyRail → setWorkspace('amm') → renderPage(
    // currentPage STANTIO)) e SUBITO DOPO clicca la voce di destinazione:
    // il render v1 stantio resta con la fetch in volo e il suo el.innerHTML
    // TARDIVO sovrascriverebbe il root React appena montato (failure-mode
    // del probe a1-01 §4). Nel mondo v1 la stessa razza si risolve "per
    // fortuna" (le fetch committano in ordine FIFO). Qui si risolve in modo
    // DETERMINISTICO: si traccia la Promise dell'orig in volo e, prima di un
    // render v3, la si ATTENDE (riserva 5s per non incagliarsi su una fetch
    // appesa), ri-verificando poi che la navigazione non sia stata superata.
    let pendingOrig = null;
    // A2: flag SINCRONO "sto dentro l'orig" — l'interceptor dei render<Vista>
    // (spec.interceptRenderName, sotto) lo legge per distinguere la chiamata
    // dal dispatch di renderPage (pass-through al v1) dalle chiamate DIRETTE
    // dei modali v1 (editRegola save/delete, apriCodaProposte close: r.1989/
    // 2033/2127 fanno self.render<Vista>(mountEl) BYPASSANDO renderPage — senza
    // redirect scriverebbero l'HTML v1 sopra il root React, failure-mode a1-01).
    // Il dispatch dell'orig chiama render<Vista> in modo SINCRONO (prima del
    // primo await): il flag intorno alla chiamata basta ed è deterministico.
    const callOrig = (self, page) => {
      wrapped.__ammV3InOrig = true;
      try { return orig.call(self, page); }
      finally { wrapped.__ammV3InOrig = false; }
    };
    wrapped = async function (page) {
      // uscita da una vista v3 montata: container libero PRIMA della wipe v1
      for (const [p, v] of views) {
        if (p !== page && v.isMounted()) {
          try { v.unmount(); } catch (e) { console.error('[amm-v3] unmount fallito:', e); }
        }
      }
      const next = views.get(page);
      if (next) {
        this.currentPage = page; // parità con l'orig (stampa R5, bridge-guard)
        if (pendingOrig) {
          const attesa = pendingOrig;
          await Promise.race([
            attesa.then(() => {}, () => {}),
            new Promise((r) => win.setTimeout(r, 5000)),
          ]);
          // nel frattempo l'utente può aver navigato altrove: questa render è superata
          if (this.currentPage !== page) return;
        }
        if (!win.document.getElementById('amm-page')) return; // parità r.1086
        try {
          next.render();
          return;
        } catch (e) {
          // mai vista bianca: si dichiara e si degrada al v1 originale
          console.error('[amm-v3] render ' + page + ' fallito — fallback al v1:', e);
          try { next.unmount(); } catch (e2) { /* root già andato */ }
          return callOrig(this, page);
        }
      }
      // pagine v1: mondo invariato (wipe compresa) — ma la Promise si traccia,
      // così un successivo render v3 sa aspettare il commit di QUESTO giro
      const p = Promise.resolve(callOrig(this, page));
      pendingOrig = p;
      const libera = () => { if (pendingOrig === p) pendingOrig = null; };
      p.then(libera, libera);
      return p;
    };
    wrapped.__ammV3Wrapped = true;
    wrapped.__ammV3Orig = orig;   // ispezionabile in collaudo (parità/rollback)
    wrapped.__ammV3Views = views;
    wrapped.__ammV3InOrig = false;
    A.renderPage = wrapped;
  }
  if (wrapped.__ammV3Views.has(spec.viewId)) return false; // vista già registrata: no-op
  wrapped.__ammV3Views.set(spec.viewId, v3);

  // ---- interceptor OPT-IN del render<Vista> (A2, per lo Scadenzario) ----
  // I modali v1 delegati fanno `if (mountEl) self.renderScadenzario(mountEl)`
  // al save/delete/chiusura-coda (r.1989/2033/2127): NON passano da renderPage.
  // Qui il metodo v1 viene sostituito da un redirect che, SOLO quando la vista
  // v3 è al comando (registrata + pagina corrente) e la chiamata NON viene dal
  // dispatch dell'orig (flag __ammV3InOrig), reindirizza su renderPage(viewId):
  // stessa via del v1 (refresh della vista), zero scritture sul container.
  // Pass-through in tutti gli altri casi (dispatch orig, fallback d'errore,
  // pagina diversa): il v1 originale resta raggiungibile e MAI in loop.
  if (spec.interceptRenderName && !A[spec.renderName].__ammV3Redirect) {
    const origRender = A[spec.renderName];
    const redirect = function () {
      const rp = this && this.renderPage;
      if (rp && rp.__ammV3Wrapped && !rp.__ammV3InOrig
        && rp.__ammV3Views && rp.__ammV3Views.has(spec.viewId)
        && this.currentPage === spec.viewId) {
        return rp.call(this, spec.viewId);
      }
      return origRender.apply(this, arguments);
    };
    redirect.__ammV3Redirect = true;
    redirect.__ammV3Orig = origRender; // ispezionabile (parità/rollback)
    A[spec.renderName] = redirect;
  }

  // catch-up: vista già attiva all'install e workspace amm visibile ⇒ il v3
  // subentra. CASO REALE (misurato in collaudo, reload autenticato): tryMount
  // di admin-module gira PRIMA di questo bundle (readyState 'interactive') ⇒
  // il renderPage v1 del restore è GIÀ PARTITO con la fetch in volo; se il v3
  // montasse subito, la continuazione async del v1 scriverebbe el.innerHTML
  // SOPRA il root React (il failure-mode del probe a1-01 §4). Qui: se il
  // container mostra ancora il 'Caricamento…' del v1, si ASPETTA il suo
  // commit (MutationObserver, con timeout di riserva) e SOLO DOPO si prende
  // il container; se il v1 ha già committato, si subentra subito.
  const body = win.document.body;
  const el = win.document.getElementById('amm-page');
  if (A.currentPage === spec.viewId && body && body.classList && body.classList.contains('workspace-amm') && el) {
    const catchUp = () => {
      try { A.renderPage(spec.viewId); } catch (e) { console.error('[amm-v3] catch-up fallito:', e); }
    };
    const v1InVolo = el.childElementCount <= 1 && /Caricamento/.test(el.textContent || '');
    const MO = win.MutationObserver;
    if (v1InVolo && typeof MO === 'function') {
      let fatto = false;
      const dopoCommitV1 = () => {
        if (fatto) return;
        fatto = true;
        try { mo.disconnect(); } catch (e) { /* già disconnesso */ }
        catchUp();
      };
      const mo = new MO(dopoCommitV1);
      mo.observe(el, { childList: true });
      // riserva: se il commit v1 non arriva (fetch appesa), si subentra comunque
      win.setTimeout(dopoCommitV1, 4000);
    } else {
      catchUp(); // v1 già committato (o MO assente): nessuna scrittura in arrivo
    }
  }

  return true;
}

// A1 — vista "Cruscotto scadenze" (pagina 'cruscotto', la vista che elenca le
// amm_scadenze — decisione registrata in a1-01). Modale scadenza, navigazione
// ricerca, badge e formattazioni DELEGATI al v1 (guardie sui metodi usati).
export function installAmmV3Cruscotto(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3CruscottoEnabled,
    renderName: 'renderCruscotto',
    viewId: 'cruscotto',
    delegatedMethods: AMM_DELEGATED_METHODS,
    delegatedApiPaths: AMM_API_PATHS,
    labelIt: 'Cruscotto',
  });
}

// A2 — vista "Regole degli obblighi" (pagina 'scadenzario': il CATALOGO delle
// regole, spike o27 §1.2 — NON le scadenze, che sono nel Cruscotto). Editor
// regola, wizard AI e coda proposte DELEGATI al v1; interceptRenderName
// perché quei modali ridipingono via renderScadenzario(mountEl) BYPASSANDO
// renderPage (r.1989/2033/2127) — il redirect li riporta nella via del wrap.
export function installAmmV3Scadenzario(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3ScadenzarioEnabled,
    renderName: 'renderScadenzario',
    viewId: 'scadenzario',
    delegatedMethods: AS_DELEGATED_METHODS,
    delegatedApiPaths: AS_API_PATHS,
    interceptRenderName: true,
    labelIt: 'Regole degli obblighi',
  });
}

// A2 — vista GENERICA "Anagrafiche": UNA vista per le 8 pagine servite dal v1
// con renderAnagrafica(el, key) (pilota DataTable della spike o27 §2.3).
// Un install PER pagina (stessa flag, stesse guardie: all-or-nothing sui path
// api condivisi), factory curried sulla key. Schede/editor/modali (anagRowClick,
// editAnagrafica, import buste AI, registra-utilizzo credito) DELEGATI al v1 —
// che dopo il save fa renderPage(key) (r.4455): passa GIÀ dal wrap, nessun
// interceptor necessario (verificato: renderAnagrafica non ha chiamanti
// fuori-dispatch nel v1 se non i suoi handler interni, che nel mondo v3 non
// esistono).
export function installAmmV3Anagrafica(win, createV3, key) {
  if (AMM_ANAG_KEYS.indexOf(key) === -1) return false; // pagina non-anagrafica: mai
  return installAmmV3View(win, createV3, {
    enabled: ammV3AnagraficheEnabled,
    renderName: 'renderAnagrafica',
    viewId: key,
    delegatedMethods: AN_DELEGATED_METHODS,
    delegatedApiPaths: AN_API_PATHS,
    // ARCHIVIABILI è una PROPRIETÀ (mappa), non un metodo: guardia dedicata —
    // senza di essa la vista non sa quali pagine hanno il tab Archiviati.
    extraGuard: (w) => !!(w.AMM && w.AMM.ARCHIVIABILI && typeof w.AMM.ARCHIVIABILI === 'object'),
    labelIt: 'Anagrafiche',
  });
}

// A3 — vista "Calendario scadenze" (pagina 'calendario': griglia mensile
// delle amm_scadenze sul componente condiviso MonthGrid). Modale scadenza e
// popover '+N altre' DELEGATI al v1. NIENTE interceptRenderName: censimento
// statico nel contract test — renderCalendario ha SOLO il dispatch e 4
// re-render interni (handler che nel mondo v3 non esistono); openEditor
// ridipinge via renderPage(currentPage) (r.7858/7888) ⇒ passa GIÀ dal wrap.
export function installAmmV3Calendario(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3CalendarioEnabled,
    renderName: 'renderCalendario',
    viewId: 'calendario',
    delegatedMethods: AC_DELEGATED_METHODS,
    delegatedApiPaths: AC_API_PATHS,
    labelIt: 'Calendario',
  });
}

// A3 — vista "Riepilogo annuale" (pagina 'riepilogo': carico fiscale
// dell'anno con STAMPA A5-039 — il beforeprint globale del v1 legge
// AMM.currentPage, che il wrap tiene aggiornato). Quadratura spiegata
// DELEGATA al v1. NIENTE interceptor: unico re-render interno è il select
// anno (censito nel contract test).
export function installAmmV3Riepilogo(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3RiepilogoEnabled,
    renderName: 'renderRiepilogo',
    viewId: 'riepilogo',
    delegatedMethods: AR_DELEGATED_METHODS,
    delegatedApiPaths: AR_API_PATHS,
    labelIt: 'Riepilogo',
  });
}

// A3 — vista "Resoconto d'insieme" (pagina 'resoconto': matrice
// tipologia×mese con semantica null-vs-0, confronto anno-1). Drill-down
// DELEGATO al v1 (openResocontoDrill: modale READ-ONLY con guardClose, non
// ridipinge la pagina). NIENTE interceptor: 3 re-render interni censiti nel
// contract test.
export function installAmmV3Resoconto(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3ResocontoEnabled,
    renderName: 'renderResoconto',
    viewId: 'resoconto',
    delegatedMethods: ARS_DELEGATED_METHODS,
    delegatedApiPaths: ARS_API_PATHS,
    labelIt: 'Resoconto',
  });
}

// A4 — vista "Costo personale" (pagina 'personale': matrice dipendente×mese dai
// cedolini + quadratura cedolini↔F24). Modale cedolino (openCedolino), badge
// stato e helpIcon DELEGATI al v1. NIENTE interceptRenderName: censimento
// STATICO nel contract test — renderPersonale ha SOLO il dispatch e 3 re-render
// interni (toggle/prev/next: handler che nel mondo v3 non esistono); il modale
// cedolino non ridipinge la pagina Personale (ha un proprio onSaved/refresh).
export function installAmmV3Personale(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3PersonaleEnabled,
    renderName: 'renderPersonale',
    viewId: 'personale',
    delegatedMethods: AP_DELEGATED_METHODS,
    delegatedApiPaths: AP_API_PATHS,
    labelIt: 'Costo personale',
  });
}

// A4 — vista "Voci & trasferte" (pagina 'voci': aggregato categoria×dipendente×
// mese sulle voci del cedolino). Classifica/export DELEGATI ai path v1. NIENTE
// interceptRenderName: renderVoci ha SOLO dispatch + 3 re-render interni
// (prev/next/chip + classifica), tutti da handler del DOM v1 (inesistenti in v3).
export function installAmmV3Voci(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3VociEnabled,
    renderName: 'renderVoci',
    viewId: 'voci',
    delegatedMethods: AV_DELEGATED_METHODS,
    delegatedApiPaths: AV_API_PATHS,
    labelIt: 'Voci & trasferte',
  });
}

// A4 — vista "Bilanci e archivio" (pagina 'esercizi': slot documenti
// d'esercizio per anno). Modale documento (editEsercizioDoc), badge e delSafe
// DELEGATI al v1. NIENTE interceptRenderName: renderEsercizi ha dispatch + i
// suoi re-render interni sono handler del DOM v1 (pillole/add/edit/del); il
// modale documento riceve dal v3 il proprio onSaved (mai renderEsercizi diretto).
export function installAmmV3Esercizi(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3EserciziEnabled,
    renderName: 'renderEsercizi',
    viewId: 'esercizi',
    delegatedMethods: AE_DELEGATED_METHODS,
    delegatedApiPaths: AE_API_PATHS,
    labelIt: 'Bilanci e archivio',
  });
}

// A5 — vista "Imposte dell'anno" (pagina 'tributi': piano imposte ad ALBERO
// categoria→tributo→voce→occorrenze, primo consumatore reale di TreeView nel
// cantiere amm). Editor dovuto (openDovuto) e box quadratura
// (openQuadraturaSpiegata) DELEGATI al v1. NIENTE interceptRenderName: censimento
// STATICO nel contract test — renderTributi ha SOLO il dispatch e i suoi
// re-render interni sono handler del DOM v1 (prev/next/collassi: inesistenti in
// v3); openDovuto ridipinge via l'onSaved che GLI PASSA il v3 (mai renderTributi
// diretto).
export function installAmmV3Tributi(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3TributiEnabled,
    renderName: 'renderTributi',
    viewId: 'tributi',
    delegatedMethods: TR_DELEGATED_METHODS,
    delegatedApiPaths: TR_API_PATHS,
    labelIt: "Imposte dell'anno",
  });
}

// A5 — vista "Piani di rientro" (pagina 'rateizzazioni': lista piani di
// rateazione). Editor "+ Nuovo" (editRateizzazione) e dettaglio con RATE
// EDITABILI (openRateizzazione) DELEGATI al v1. NIENTE interceptRenderName:
// censimento STATICO — renderRateizzazioni ha SOLO dispatch + 2 re-render
// interni (new/del: handler del DOM v1); i modali usano onSaved/reload proprio
// (mai renderRateizzazioni diretto).
export function installAmmV3Rateizzazioni(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3RateizzazioniEnabled,
    renderName: 'renderRateizzazioni',
    viewId: 'rateizzazioni',
    delegatedMethods: RZ_DELEGATED_METHODS,
    delegatedApiPaths: RZ_API_PATHS,
    labelIt: 'Piani di rientro',
  });
}

// A5 — vista interna "Costi per cantiere" (pagina 'cantieri-costi': aggregato
// read-only, raggiunta dalla pagina Cantieri). SOLA LETTURA, nessun modale.
// NIENTE interceptRenderName: renderCantieriCosti ha SOLO il dispatch (i suoi
// prev/next usano renderPage('cantieri-costi'), che passa GIÀ dal wrap).
export function installAmmV3CantieriCosti(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3CantieriCostiEnabled,
    renderName: 'renderCantieriCosti',
    viewId: 'cantieri-costi',
    delegatedMethods: CC_DELEGATED_METHODS,
    delegatedApiPaths: CC_API_PATHS,
    labelIt: 'Costi per cantiere',
  });
}

// A6 — vista "Profilo azienda" (pagina 'profilo', l'HUB: scheda azienda + 8
// flag "Caratteristiche fiscali" + anteprima impatto (dry-run) + salva-e-
// rigenera + le sotto-sezioni tabellari Tipologie/Parametri/Proroghe NATIVE,
// coi pannelli Promemoria/Registro/Spazio/Cestino DELEGATI al v1). SEAM =
// renderProfilo, viewId 'profilo': l'INTERA pagina è UNA vista React (flag
// UNICA — nessun dispatch per-sotto-sezione). NIENTE interceptRenderName:
// censimento STATICO nel contract test — renderProfilo ha SOLO il dispatch (le
// sotto-sezioni sono metodi separati, non re-render di renderProfilo); i modali
// editTipologia/editParametro/editProroga usano l'onSaved che GLI PASSA il v3
// (refresh della sola sezione, mai renderProfilo diretto).
export function installAmmV3Profilo(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3ProfiloEnabled,
    renderName: 'renderProfilo',
    viewId: 'profilo',
    delegatedMethods: PR_DELEGATED_METHODS,
    delegatedApiPaths: PR_API_PATHS,
    labelIt: 'Profilo azienda',
  });
}

// A7 — vista "Pagamenti F24" (pagina 'f24': la LISTA dei modelli F24/versamenti,
// SETTIMA e ULTIMA vista del cantiere — chiude AMM 21/21). SEAM = renderF24,
// viewId 'f24'. Header 4 bottoni + 3 KPI + barra filtri + tabella NATIVI React; il
// WIZARD AI di import quietanze (openImportQuietanza), l'editor testata (editF24),
// il ravvedimento (openRavvedimento), il dettaglio (openF24) e l'elimina
// (delSafe+messaggioCollegamenti) restano DELEGATI al v1 — il wizard è troppo
// accoppiato e già SICURO su tutte le invarianti d'import (confine dichiarato,
// come le rate in A5). NIENTE interceptRenderName: censimento STATICO nel contract
// test — renderF24 ha SOLO il dispatch e 4 re-render interni (delete-onOk + 3
// onSaved dei modali, tutti closure DENTRO il corpo v1: inesistenti nel mondo v3);
// i modali ricevono l'onSaved che GLI PASSA il v3, openF24 si auto-gestisce.
export function installAmmV3F24(win, createV3) {
  return installAmmV3View(win, createV3, {
    enabled: ammV3F24Enabled,
    renderName: 'renderF24',
    viewId: 'f24',
    delegatedMethods: F24_DELEGATED_METHODS,
    delegatedApiPaths: F24_API_PATHS,
    labelIt: 'Pagamenti F24',
  });
}
